/*
 * cart-retry.js — AO Tea
 *
 * Shopify answers with 5xx when it applies a temporary throttle, and the
 * Baseline bundle calls `response.json()` on the result without checking the
 * status: the HTML error page fails to parse, the handler dies, and the button
 * silently does nothing. This wrapper retries the cart request instead.
 *
 * Loaded before the theme bundles so that it wraps `fetch` first. It touches
 * nothing in the vendor bundles, so a Baseline upgrade cannot overwrite it.
 *
 * Safety rule: a request is only replayed when replaying it cannot change the
 * cart twice. Reads and absolute-quantity writes qualify. Add-to-cart does not,
 * so before replaying one we read the cart back and replay only when the item
 * is not there.
 */
(function () {
  var w = typeof window !== 'undefined' ? window : globalThis;
  if (w.__aoCartRetryInstalled) return;

  var nativeFetch = w.fetch;
  if (typeof nativeFetch !== 'function') return;
  w.__aoCartRetryInstalled = true;

  var DELAYS = Array.isArray(w.AO_CART_RETRY_DELAYS) ? w.AO_CART_RETRY_DELAYS : [400, 1200];
  var LOCALE_PREFIX = /^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i;

  // Last cart we saw come back from the server, used as the add-to-cart baseline.
  var lastCart = null;

  function sleep(ms) {
    return new Promise(function (resolve) { w.setTimeout(resolve, ms); });
  }

  function parseUrl(url) {
    try {
      return new URL(url, w.location.origin);
    } catch (e) {
      return null;
    }
  }

  /* 'safe' = replayable as-is, 'add' = replayable only after verification. */
  function classify(parsed, method) {
    if (!parsed || parsed.origin !== w.location.origin) return null;
    var path = parsed.pathname.replace(LOCALE_PREFIX, '');
    if (method === 'GET') {
      if (path === '/cart.js' || path === '/cart') return 'safe';
      if (parsed.searchParams.has('section_id') || parsed.searchParams.has('sections')) return 'safe';
      return null;
    }
    if (method === 'POST') {
      if (/^\/cart\/(change|update|clear)(\.js)?$/.test(path)) return 'safe';
      if (/^\/cart\/add(\.js)?$/.test(path)) return 'add';
    }
    return null;
  }

  function localeRoot(parsed) {
    var match = parsed.pathname.match(LOCALE_PREFIX);
    return match ? match[0] : '';
  }

  function variantIdFrom(body) {
    if (!body) return null;
    try {
      if (typeof body.get === 'function') return body.get('id');   // FormData, URLSearchParams
      if (typeof body !== 'string') return null;
      try {
        var json = JSON.parse(body);
        if (json && json.items && json.items.length) return json.items[0].id;
        if (json && json.id) return json.id;
        return null;
      } catch (e) {
        var params = new w.URLSearchParams(body);
        return params.get('id');
      }
    } catch (e) {
      return null;
    }
  }

  function quantityOf(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var wanted = String(variantId);
    return cart.items.reduce(function (total, item) {
      var id = item.variant_id != null ? item.variant_id : item.id;
      return String(id) === wanted ? total + (item.quantity || 0) : total;
    }, 0);
  }

  /*
   * Records the cart carried by a successful response and resolves with that
   * same response. Awaited rather than fired off, so a later add-to-cart always
   * has the freshest baseline to compare against.
   */
  function rememberCart(response) {
    if (!response || !response.ok) return Promise.resolve(response);
    return response.clone().json().then(function (body) {
      if (body && Array.isArray(body.items)) lastCart = body;
      return response;
    }).catch(function () {
      return response; // not cart JSON — ignore
    });
  }

  /* True when the failed add did NOT reach the cart, so replaying it is safe. */
  function addIsMissing(parsed, variantId, baseline) {
    return nativeFetch(localeRoot(parsed) + '/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      if (!response.ok) return false;
      return response.json();
    }).then(function (cart) {
      if (!cart) return false;
      var now = quantityOf(cart, variantId);
      if (now === null) return false;
      return baseline === null ? now === 0 : now <= baseline;
    }).catch(function () {
      return false;
    });
  }

  w.fetch = function (input, init) {
    // A Request object may carry an already-consumed body, so never replay one.
    if (typeof input !== 'string') return nativeFetch.apply(this, arguments);

    var method = String((init && init.method) || 'GET').toUpperCase();
    var parsed = parseUrl(input);
    var kind = classify(parsed, method);
    if (!kind) return nativeFetch.apply(this, arguments);

    var self = this;
    var args = arguments;
    var variantId = kind === 'add' ? variantIdFrom(init && init.body) : null;
    if (kind === 'add' && variantId == null) return nativeFetch.apply(self, args);
    var baseline = kind === 'add' ? quantityOf(lastCart, variantId) : null;
    var attempt = 0;

    function aborted() {
      return !!(init && init.signal && init.signal.aborted);
    }

    function attemptOnce() {
      return nativeFetch.apply(self, args).then(function (response) {
        if (response.status < 500 || attempt >= DELAYS.length || aborted()) {
          return rememberCart(response);
        }
        var delay = DELAYS[attempt];
        attempt += 1;
        if (kind === 'safe') {
          return sleep(delay).then(attemptOnce);
        }
        return addIsMissing(parsed, variantId, baseline).then(function (missing) {
          if (!missing) return response;          // it landed — never add it twice
          return sleep(delay).then(attemptOnce);
        });
      });
    }

    return attemptOnce();
  };
})();
